import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Result,
  Segmented,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  AlertOutlined,
  ClusterOutlined,
  CustomerServiceOutlined,
  FileProtectOutlined,
  IdcardOutlined,
  MailOutlined,
  ReloadOutlined,
  SaveOutlined,
  SafetyCertificateOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import './ops-email-recipients.css';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const LIST_KEYS = [
  'AMAN_CS_EMAIL',
  'AMAN_CLAIMS_EMAIL',
  'AMAN_FINANCE_EMAIL',
  'AMAN_RENEWAL_EMAILS',
  'AMAN_POLICY_OPS_EMAILS',
  'FACILITY_CREDENTIAL_CC_EMAILS',
  'ORG_APPROVAL_NOTIFY_EMAILS',
  'BULK_UPLOAD_NOTIFY_EMAILS',
  'ORG_QUEUE_NOTIFY_EXTRA_EMAILS',
  'AMAN_ADMIN_ALERT_EMAILS',
];

/**
 * Normalize form values to the saveable draft shape (matches PUT payload semantics).
 * Source mode / RBAC values are only meaningful when their override switch is on.
 */
const normalizeDraft = (values = {}) => {
  const lists = {};
  LIST_KEYS.forEach((key) => {
    lists[key] = (values[key] ?? '').toString().trim();
  });
  const sourceModeUseOverride = !!values._source_mode_use_override;
  const rbacUseOverride = !!values._rbac_use_override;
  return {
    lists,
    sourceModeUseOverride,
    sourceMode: sourceModeUseOverride ? String(values.source_mode || 'both') : '',
    rbacUseOverride,
    rbacMerge: rbacUseOverride ? !!values.AMAN_INTERNAL_EMAIL_RBAC_MERGE : null,
  };
};

const draftsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Plain-English titles + explanations (env key stays visible in the UI).
 * Triggers verified against InternalEmailNotifier / FacilityOnboarding / OrgQueueNotify.
 */
const LIST_COPY = {
  AMAN_CS_EMAIL: {
    title: 'Customer service alerts',
    help: 'Notified when a PA is filed or updated, and when an enrollee is pending activation.',
  },
  AMAN_CLAIMS_EMAIL: {
    title: 'Claims ops alerts',
    help: 'Notified on facility claim submissions and related claims events. Env falls back to OFFICIAL_CLAIMS_EMAIL if this key is empty.',
  },
  AMAN_FINANCE_EMAIL: {
    title: 'Finance notices',
    help: 'Notified for finance-related events: client/org policy deactivation and claim financial notices.',
  },
  AMAN_RENEWAL_EMAILS: {
    title: 'Policy renewal reminders',
    help: 'Notified for policy pre-expiry and renewal notices (org premium approaching expiry, self-pay pre-expiry).',
  },
  AMAN_POLICY_OPS_EMAILS: {
    title: 'Policy ops (always included)',
    help: 'Always added on policy expiry and disable internal alerts, alongside the primary group (renewal or finance).',
  },
  FACILITY_CREDENTIAL_CC_EMAILS: {
    title: 'Facility credential CC',
    help: "CC'd on facility portal credential / welcome emails when login details are sent.",
  },
  ORG_APPROVAL_NOTIFY_EMAILS: {
    title: 'Org queue (care-admin)',
    help: 'Account-team alerts for care-admin queue items: pending plan requests and staff uploads. If empty, falls back to Bulk upload completion.',
  },
  BULK_UPLOAD_NOTIFY_EMAILS: {
    title: 'Bulk upload completion',
    help: 'Recipients for HR bulk-upload completion notices; also used as fallback when Org queue (care-admin) is empty.',
  },
  ORG_QUEUE_NOTIFY_EXTRA_EMAILS: {
    title: 'Org outcome extras',
    help: "Extra recipients added to org-facing approve/reject outcome emails (alongside the organization's own email).",
  },
  AMAN_ADMIN_ALERT_EMAILS: {
    title: 'System outage alerts',
    help: 'Emailed directly by the notification watchdog when the notification system itself stops, and again when it recovers. With no address here an outage is only logged and shown as a warning in the Admin Portal; nobody is emailed.',
  },
};

/** Group lists by purpose for scannable admin UI. */
const LIST_SECTIONS = [
  {
    id: 'cs',
    title: 'Customer service',
    icon: <CustomerServiceOutlined />,
    keys: ['AMAN_CS_EMAIL'],
  },
  {
    id: 'claims',
    title: 'Claims',
    icon: <FileProtectOutlined />,
    keys: ['AMAN_CLAIMS_EMAIL'],
  },
  {
    id: 'finance',
    title: 'Finance',
    icon: <WalletOutlined />,
    keys: ['AMAN_FINANCE_EMAIL'],
  },
  {
    id: 'policy',
    title: 'Policy & renewal',
    icon: <SafetyCertificateOutlined />,
    keys: ['AMAN_RENEWAL_EMAILS', 'AMAN_POLICY_OPS_EMAILS'],
  },
  {
    id: 'facility',
    title: 'Facility credentials',
    icon: <IdcardOutlined />,
    keys: ['FACILITY_CREDENTIAL_CC_EMAILS'],
  },
  {
    id: 'org',
    title: 'Org queues',
    icon: <ClusterOutlined />,
    keys: [
      'ORG_APPROVAL_NOTIFY_EMAILS',
      'BULK_UPLOAD_NOTIFY_EMAILS',
      'ORG_QUEUE_NOTIFY_EXTRA_EMAILS',
    ],
  },
  {
    id: 'system',
    title: 'System outage alerts',
    icon: <AlertOutlined />,
    keys: ['AMAN_ADMIN_ALERT_EMAILS'],
  },
];

const SOURCE_MODE_OPTIONS = [
  { value: 'env', label: 'Env only' },
  { value: 'db', label: 'Database only' },
  { value: 'both', label: 'Both (merge)' },
];

const SOURCE_MODE_PLAIN = {
  env: 'Use only addresses from server environment variables (values saved here are ignored).',
  db: 'Use only addresses saved on this page (env list values are ignored).',
  both: 'Combine env and database addresses for each list (duplicates removed).',
};

const sourceBadgeClass = {
  override: 'ops-email-badge--override',
  db: 'ops-email-badge--db',
  env: 'ops-email-badge--env',
  merged: 'ops-email-badge--merged',
  empty: 'ops-email-badge--empty',
  default: 'ops-email-badge--default',
};

const sourceLabel = {
  override: 'Database',
  db: 'Database',
  env: 'Env',
  merged: 'Merged',
  empty: 'Empty',
  default: 'Default',
};

const formatEmails = (list) => {
  if (!Array.isArray(list) || !list.length) return 'None';
  return list.join(', ');
};

const SourceBadge = ({ source }) => {
  const key = source || 'empty';
  return (
    <Tag className={`ops-email-badge ${sourceBadgeClass[key] || sourceBadgeClass.default}`}>
      {sourceLabel[key] || key}
    </Tag>
  );
};

const ListField = ({ listKey, info }) => {
  const copy = LIST_COPY[listKey] || {};
  const title = copy.title || info.label || listKey;
  const help = copy.help || info.help || '';
  const effective = Array.isArray(info.effective) ? info.effective : [];
  const fromEnv = Array.isArray(info.from_env) ? info.from_env : [];
  const tooltip = (
    <div style={{ fontSize: 12, lineHeight: 1.5 }}>
      <div>
        Env{info.env_legacy_alias ? ' (via OFFICIAL_CLAIMS_EMAIL)' : ''}:{' '}
        {fromEnv.length ? fromEnv.join(', ') : '(not set)'}
      </div>
      <div>Effective: {effective.length ? effective.join(', ') : '(none)'}</div>
    </div>
  );

  return (
    <div className="ops-email-list">
      <div className="ops-email-list__head">
        <div>
          <Title level={5} className="ops-email-list__label">
            {title}
          </Title>
          <Text className="ops-email-list__key">{listKey}</Text>
        </div>
        <Tooltip title={tooltip}>
          <span>
            <SourceBadge source={info.source} />
          </span>
        </Tooltip>
      </div>
      {help ? <Paragraph className="ops-email-list__help">{help}</Paragraph> : null}
      <Form.Item name={listKey} noStyle>
        <TextArea
          rows={2}
          placeholder="Comma-separated emails — used when source is db or both"
          allowClear
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </Form.Item>
      <div className="ops-email-list__foot">
        <Text className="ops-email-list__effective">
          Effective:{' '}
          <strong>{formatEmails(effective)}</strong>
        </Text>
      </div>
    </div>
  );
};

const OpsEmailRecipientsPage = () => {
  const { hasAnyPermission } = usePermissions();
  const allowed = useMemo(
    () => hasAnyPermission(['admin.*', 'admin.system_config']),
    [hasAnyPermission]
  );

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const baselineRef = useRef(null);
  const [meta, setMeta] = useState({ updated_by: null, updated_at: null, resolution_order: '' });
  const [listsMeta, setListsMeta] = useState({});
  const [rbacMeta, setRbacMeta] = useState(null);
  const [sourceModeMeta, setSourceModeMeta] = useState(null);

  const markDirty = useCallback(() => {
    setDirty(true);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getOpsEmailRecipients();
      if (resp?.error) {
        // Unlock Save even if GET failed so the user can still attempt PUT.
        setLoaded(true);
        return;
      }
      const data = resp?.data?.data || resp?.data || {};
      const lists = data.lists || {};
      const values = {};
      LIST_KEYS.forEach((key) => {
        // Coerce null overrides to '' so controlled TextAreas stay stable vs baseline.
        values[key] = lists[key]?.override ?? '';
      });
      const rbac = data.rbac_merge || {};
      const rbacUseOverride = rbac.source === 'override';
      values._rbac_use_override = rbacUseOverride;
      // Always a boolean when override is on; false placeholder when following env
      // (ignored by normalizeDraft / save when override is off).
      values.AMAN_INTERNAL_EMAIL_RBAC_MERGE = rbacUseOverride
        ? !!rbac.override
        : !!rbac.env;

      const sm = data.source_mode || {};
      values._source_mode_use_override = sm.source === 'override';
      values.source_mode = sm.override || sm.effective || sm.default || 'both';

      baselineRef.current = normalizeDraft(values);
      setDirty(false);
      form.setFieldsValue(values);
      setListsMeta(lists);
      setRbacMeta(rbac);
      setSourceModeMeta(sm);
      setMeta({
        updated_by: data.updated_by ?? null,
        updated_at: data.updated_at ?? null,
        resolution_order: data.resolution_order || '',
      });
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const handleSave = async () => {
    try {
      // Validate mounted fields; then read full store (incl. conditionally unmounted).
      await form.validateFields();
      const values = form.getFieldsValue(true);

      // Soft no-op when nothing actually changed vs last successful load.
      if (baselineRef.current && draftsEqual(normalizeDraft(values), baselineRef.current)) {
        notification.info({
          message: 'No changes to save',
          description: 'Recipient lists and overrides match what is already loaded.',
        });
        setDirty(false);
        return;
      }

      setSaving(true);
      const payload = {};
      LIST_KEYS.forEach((key) => {
        payload[key] = (values[key] ?? '').toString().trim();
      });
      if (values._rbac_use_override) {
        payload.AMAN_INTERNAL_EMAIL_RBAC_MERGE = !!values.AMAN_INTERNAL_EMAIL_RBAC_MERGE;
      } else {
        // Clear UI override → fall back to env
        payload.AMAN_INTERNAL_EMAIL_RBAC_MERGE = '';
      }
      if (values._source_mode_use_override) {
        payload.source_mode = values.source_mode || 'both';
      } else {
        // Clear DB override → fall back to env AMAN_OPS_EMAIL_SOURCE → default both
        payload.source_mode = '';
      }
      const resp = await lib.updateOpsEmailRecipients(payload);
      if (resp?.error) return;
      notification.success({
        message: resp?.data?.message || 'Ops email recipients saved',
        description: 'Source mode and lists take effect immediately for new emails.',
      });
      await load();
    } catch (err) {
      // validateFields rejects with errorFields — Form shows them.
      if (err?.errorFields) return;
      notification.error({
        message: 'Could not save ops email recipients',
        description: err?.message || 'Unexpected error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Enable once loaded — do not gate on dirty (Effective/env can make the form look
  // "filled" while override fields are unchanged, so dirty comparison stays false).
  const canSave = loaded && !saving && !loading;

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admin only"
        subTitle="Operational email recipient lists require admin.* or admin.system_config."
      />
    );
  }

  const effectiveMode = sourceModeMeta?.effective || 'both';
  const lastChanged = meta.updated_at
    ? `Last changed ${meta.updated_at}${meta.updated_by ? ` by staff #${meta.updated_by}` : ''}`
    : 'No database overrides yet';

  return (
    <div className="ops-email-page">
      <header className="ops-email-page__header">
        <div>
          <div className="ops-email-page__title-row">
            <span className="ops-email-page__icon" aria-hidden>
              <MailOutlined />
            </span>
            <div>
              <Title level={3} className="ops-email-page__title">
                Ops email recipients
              </Title>
              <Paragraph className="ops-email-page__desc">
                Who gets internal ops emails — and for which events. Pick whether addresses
                come from env, this page, or both, then edit each list below. By default only
                these lists are used; role-based staff fan-out is opt-in below.
              </Paragraph>
            </div>
          </div>
        </div>
        <Text className="ops-email-page__meta">{lastChanged}</Text>
      </header>

      <Spin spinning={loading && !loaded}>
        {!loaded && loading ? (
          <div className="ops-email-card">
            <div className="ops-email-empty">
              <Title level={5} className="ops-email-empty__title">
                Loading recipient settings…
              </Title>
              <Paragraph className="ops-email-empty__desc">
                Fetching source mode and distribution lists.
              </Paragraph>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            className="ops-email-page__stack"
            requiredMark={false}
            onValuesChange={markDirty}
          >
            {/* Source mode */}
            <section className="ops-email-card">
              <div className="ops-email-card__body">
                <Title level={5} className="ops-email-card__title">
                  Recipient source
                </Title>
                <Paragraph className="ops-email-card__hint">
                  <strong>Env only</strong> — server environment variables (values here are
                  ignored). <strong>Database only</strong> — addresses saved on this page.{' '}
                  <strong>Both (merge)</strong> — combine env + this page (duplicates removed).
                </Paragraph>

                <div className="ops-email-source-row">
                  <div className="ops-email-source-row__label">
                    <strong>Override env for source mode</strong>
                    <span>
                      {sourceModeMeta
                        ? `Env: ${sourceModeMeta.env || sourceModeMeta.env_raw || '(not set → default both)'}`
                        : 'Falls back to AMAN_OPS_EMAIL_SOURCE, then both.'}
                    </span>
                  </div>
                  <Form.Item name="_source_mode_use_override" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Override" unCheckedChildren="Use env" />
                  </Form.Item>
                </div>

                <Form.Item shouldUpdate noStyle>
                  {() =>
                    form.getFieldValue('_source_mode_use_override') ? (
                      <div className="ops-email-source-mode">
                        <Form.Item
                          name="source_mode"
                          rules={[{ required: true, message: 'Select a source mode' }]}
                        >
                          <Segmented options={SOURCE_MODE_OPTIONS} block />
                        </Form.Item>
                        <Paragraph className="ops-email-card__hint" style={{ marginTop: 10, marginBottom: 0 }}>
                          {SOURCE_MODE_PLAIN[form.getFieldValue('source_mode')] || ''}
                        </Paragraph>
                      </div>
                    ) : null
                  }
                </Form.Item>

                {sourceModeMeta ? (
                  <div className="ops-email-effective-chip">
                    Effective mode: <strong>{sourceModeMeta.effective}</strong>
                    <span style={{ opacity: 0.8 }}>
                      · via {sourceModeMeta.source}
                      {sourceModeMeta.source === 'env' && sourceModeMeta.env_raw
                        ? ` (${sourceModeMeta.env_raw})`
                        : ''}
                      {sourceModeMeta.source === 'default' ? ' (both)' : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            {effectiveMode === 'env' ? (
              <Alert
                type="warning"
                showIcon
                message="Env-only mode"
                description="Database list values below are stored but ignored while effective source mode is env. Switch to Database only or Both to use them."
              />
            ) : null}

            {/* Grouped recipient lists */}
            {LIST_SECTIONS.map((section) => (
              <section key={section.id} className="ops-email-section">
                <div className="ops-email-section__head">
                  <Space size={6}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {section.icon}
                    </Text>
                    <Title level={5} className="ops-email-section__title">
                      {section.title}
                    </Title>
                  </Space>
                  <span className="ops-email-section__count">
                    {section.keys.length} list{section.keys.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="ops-email-card">
                  {section.keys.map((key) => (
                    <ListField key={key} listKey={key} info={listsMeta[key] || {}} />
                  ))}
                </div>
              </section>
            ))}

            {/* RBAC merge — opt-in; default off so lists alone control delivery */}
            <section className="ops-email-card">
              <div className="ops-email-card__body">
                <Title level={5} className="ops-email-card__title">
                  Also email staff by role (RBAC merge)
                </Title>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Opt-in — can email far more people than intended"
                  description={
                    <>
                      Leave this off so only the distribution lists above receive CS /
                      claims / finance / renewal mail. Turning it on unions every active
                      staff member with matching roles (e.g. CS: customer_service, admin,
                      super_admin; claims: claims_manager, claims_officer, medical_officer).
                      Use only if you want role-based fan-out, or empty lists + RBAC-only
                      delivery. Org-queue and facility-CC lists are never RBAC-merged.
                      {rbacMeta
                        ? ` Effective now: ${rbacMeta.effective ? 'on' : 'off'} (${rbacMeta.source}).`
                        : ''}
                    </>
                  }
                />
                <Text className="ops-email-list__key" style={{ display: 'block', marginBottom: 12 }}>
                  AMAN_INTERNAL_EMAIL_RBAC_MERGE
                </Text>

                <div className="ops-email-rbac-row">
                  <div className="ops-email-rbac-row__text">
                    <strong>Override env for RBAC merge</strong>
                    <span>
                      {rbacMeta
                        ? `Env (default off): ${rbacMeta.env ? 'on' : 'off'}. Clear override to follow env.`
                        : 'Clear override to follow env (default off).'}
                    </span>
                  </div>
                  <Form.Item name="_rbac_use_override" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Override" unCheckedChildren="Use env" />
                  </Form.Item>
                </div>

                <Form.Item shouldUpdate noStyle>
                  {() =>
                    form.getFieldValue('_rbac_use_override') ? (
                      <div className="ops-email-rbac-row">
                        <div className="ops-email-rbac-row__text">
                          <strong>Include matching role staff</strong>
                          <span>
                            Dangerous: adds every active staff email for the group’s roles
                            on top of the lists above.
                          </span>
                        </div>
                        <Form.Item
                          name="AMAN_INTERNAL_EMAIL_RBAC_MERGE"
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch checkedChildren="On" unCheckedChildren="Off" />
                        </Form.Item>
                      </div>
                    ) : null
                  }
                </Form.Item>
              </div>
            </section>
          </Form>
        )}
      </Spin>

      <div className="ops-email-action-bar">
        <div className="ops-email-action-bar__inner">
          <Text className="ops-email-action-bar__hint">
            {dirty
              ? 'You have unsaved changes.'
              : 'Changes apply immediately to new operational emails.'}
          </Text>
          <div className="ops-email-action-bar__actions">
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} disabled={saving}>
              Reload
            </Button>
            <Button
              type="primary"
              htmlType="button"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!canSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsEmailRecipientsPage;
